/* eslint-disable */
/**
 * Seed dataset for local UI work — LOCAL DEV ONLY.
 *
 * This module is only ever imported when VITE_USE_MOCK === 'true'. It never
 * ships to production: the real Supabase client is still the default export
 * path in src/lib/supabase.js when the flag is absent.
 *
 * Shapes here mirror supabase/migrations exactly (snake_case, same enums), so
 * components and services need zero changes to run against it.
 *
 * Deliberate edge cases are seeded throughout so layout breakage shows up
 * during design work rather than in production:
 *   - very long paper titles and author lists (8 authors)
 *   - Arabic / RTL titles and names
 *   - missing avatars, missing optional fields, null decisions
 *   - a conference with zero submissions (empty states)
 *   - submissions with 0, 1, 2 and 3 completed reviews (progress states)
 */

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** Deterministic, valid-looking v4 UUID so React keys and FK joins stay stable. */
export const uid = (seed) => {
  const h = String(seed).padStart(12, '0').slice(-12);
  return `00000000-0000-4000-8000-${h}`;
};

const DAY = 86400000;
const now = new Date('2026-08-08T10:00:00Z').getTime();
const iso = (offsetDays = 0) => new Date(now + offsetDays * DAY).toISOString();

/** Tiny deterministic PRNG so the dataset is identical on every reload. */
const rng = (() => {
  let s = 42;
  return () => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
})();

const pick = (arr) => arr[Math.floor(rng() * arr.length)];

// ---------------------------------------------------------------------------
// users
// ---------------------------------------------------------------------------

const U = (n) => uid(1000 + n);

export const CURRENT_USER_IDS = {
  admin: U(1),
  chair: U(2),
  reviewer: U(3),
  author: U(4),
};

const userSeed = [
  // [n, first, last, email, roles, institution, country, avatar?]
  [1, 'Amine', 'Belkacem', 'admin@archeditech.com', ['organizer', 'chair', 'reviewer'], 'ArchediTech', 'Algeria', true],
  [2, 'Nadia', 'Cherif', 'chair@archeditech.com', ['chair', 'reviewer'], 'USTHB — Université des Sciences et de la Technologie Houari Boumediene', 'Algeria', true],
  [3, 'Karim', 'Haddad', 'reviewer@archeditech.com', ['reviewer', 'author'], 'CERIST', 'Algeria', false],
  [4, 'Sofia', 'Mansouri', 'author@archeditech.com', ['author'], 'University of Oran 1', 'Algeria', true],
  [5, 'Yacine', 'Bouzid', 'y.bouzid@univ-alger.dz', ['reviewer'], 'University of Algiers', 'Algeria', false],
  [6, 'Leila', 'Ferhat', 'l.ferhat@ensia.dz', ['reviewer', 'author'], 'ENSIA', 'Algeria', true],
  [7, 'Mohamed', 'Saidi', 'm.saidi@univ-constantine.dz', ['reviewer'], 'University of Constantine 2', 'Algeria', false],
  [8, 'Ines', 'Berrada', 'i.berrada@um5.ac.ma', ['author', 'reviewer'], 'Mohammed V University', 'Morocco', true],
  [9, 'Thomas', 'Lefevre', 't.lefevre@inria.fr', ['reviewer'], 'INRIA', 'France', true],
  [10, 'Amira', 'Toumi', 'a.toumi@enit.utm.tn', ['author'], 'ENIT', 'Tunisia', false],
  [11, 'Hakim', 'Ouali', 'h.ouali@univ-tlemcen.dz', ['author', 'reviewer'], 'University of Tlemcen', 'Algeria', false],
  [12, 'سارة', 'بن عمر', 's.benomar@univ-annaba.dz', ['author'], 'جامعة باجي مختار عنابة', 'Algeria', false],
  [13, 'Julia', 'Novak', 'j.novak@tum.de', ['reviewer'], 'Technical University of Munich', 'Germany', true],
  [14, 'Reda', 'Khelifi', 'r.khelifi@univ-setif.dz', ['author'], 'University of Sétif 1', 'Algeria', false],
  [15, 'Fatima', 'Zerrouki', 'f.zerrouki@usthb.dz', ['author', 'reviewer'], 'USTHB', 'Algeria', true],
];

// Offline avatar: initials on a deterministic muted background, as a data URI.
// Avoids any third-party image request from the mock environment.
const AVATAR_BG = ['#334155', '#3f3f46', '#44403c', '#374151', '#4c1d95', '#164e63'];
const avatarDataUri = (first, last, n) => {
  const initials = `${(first || '?')[0]}${(last || '')[0] || ''}`;
  const bg = AVATAR_BG[n % AVATAR_BG.length];
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160">` +
    `<rect width="160" height="160" fill="${bg}"/>` +
    `<text x="80" y="80" fill="#e5e7eb" font-family="system-ui,sans-serif" font-size="64" ` +
    `font-weight="600" text-anchor="middle" dominant-baseline="central">${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export const user_profiles = userSeed.map(([n, first, last, email, roles, institution, country, hasAvatar]) => ({
  id: U(n),
  email,
  title: n % 3 === 0 ? 'Dr.' : n % 4 === 0 ? 'Prof.' : null,
  first_name: first,
  last_name: last,
  phone: n === 1 ? '+213 555 01 02 03' : null,
  country,
  institution,
  institution_type: n % 5 === 0 ? 'research' : 'university',
  department: n % 3 === 0 ? 'Computer Science' : 'Information Systems',
  academic_rank: pick(['assistant', 'associate', 'full', 'researcher', 'postdoc']),
  orcid: n % 4 === 0 ? `0000-0002-${1000 + n}-000X` : null,
  website: n % 6 === 0 ? 'https://example.edu/~profile' : null,
  subject_areas: ['Machine Learning', 'Software Engineering', 'Networks'].slice(0, (n % 3) + 1),
  roles,
  // Platform admin is global (analytics/stats) and deliberately NOT a
  // conference role — those live in conference_roles, per conference.
  is_platform_admin: n === 1,
  experience_level: pick(['intermediate', 'advanced', 'expert']),
  // Deliberately missing for several users so avatar fallbacks get exercised.
  // Inline SVG data URI keeps the mock fully offline (no third-party requests).
  avatar_url: hasAvatar ? avatarDataUri(first, last, n) : null,
  bio: n % 4 === 0 ? 'Researcher working on distributed systems and applied machine learning.' : null,
  receive_updates: n % 2 === 0,
  available_as_reviewer: roles.includes('reviewer'),
  reviewer_keywords: roles.includes('reviewer') ? ['machine learning', 'security', 'HCI'].slice(0, (n % 3) + 1) : [],
  is_active: n !== 14, // one deactivated account
  created_at: iso(-300 + n * 4),
  updated_at: iso(-10),
}));

// ---------------------------------------------------------------------------
// conferences
// ---------------------------------------------------------------------------

const C = (n) => uid(2000 + n);

export const CONFERENCE_IDS = { active: C(1), past: C(2), empty: C(3) };

export const conferences = [
  {
    id: C(1),
    name: 'International Conference on Applied Computing and Data Science',
    short_name: 'ICACDS 2026',
    description:
      'ICACDS brings together researchers and practitioners working on applied computing, data science and their use in scientific and industrial settings.',
    call_for_papers:
      'We invite original contributions on machine learning, data engineering, distributed systems, security and human-computer interaction.',
    call_for_papers_url: null,
    website: 'https://icacds.example.dz',
    banner_image_url: null,
    location_type: 'hybrid',
    venue_name: 'Centre de Conventions d’Oran',
    location: 'Oran, Algeria',
    start_date: iso(60),
    end_date: iso(63),
    abstract_deadline: iso(-20),
    paper_deadline: iso(-5),
    review_deadline: iso(14),
    decision_date: iso(25),
    camera_ready_deadline: iso(40),
    status: 'review_phase',
    subject_area: 'Computer Science',
    conference_type: 'international',
    visibility: 'public',
    featured: true,
    public_registration: true,
    min_reviews: 3,
    enable_bidding: true,
    double_blind: true,
    show_other_reviews: false,
    keywords: ['machine learning', 'data science', 'distributed systems'],
    allowed_file_types: 'pdf',
    billing_settled: true,
    created_by: U(2),
    created_at: iso(-180),
    updated_at: iso(-2),
  },
  {
    id: C(2),
    name: 'National Symposium on Information Systems',
    short_name: 'NSIS 2025',
    description: 'A national forum on information systems research and practice.',
    call_for_papers: 'Closed.',
    call_for_papers_url: null,
    website: null,
    banner_image_url: null,
    location_type: 'in_person',
    venue_name: 'Université d’Alger 1',
    location: 'Algiers, Algeria',
    start_date: iso(-200),
    end_date: iso(-198),
    abstract_deadline: iso(-280),
    paper_deadline: iso(-260),
    review_deadline: iso(-235),
    decision_date: iso(-225),
    camera_ready_deadline: iso(-210),
    status: 'completed',
    subject_area: 'Information Systems',
    conference_type: 'national',
    visibility: 'public',
    featured: false,
    public_registration: false,
    min_reviews: 2,
    enable_bidding: false,
    double_blind: false,
    show_other_reviews: true,
    keywords: ['information systems'],
    allowed_file_types: 'pdf',
    billing_settled: true,
    created_by: U(2),
    created_at: iso(-400),
    updated_at: iso(-200),
  },
  {
    // Intentionally empty: drives every empty state in the organizer flow.
    id: C(3),
    name: 'Workshop on Research Data Sovereignty',
    short_name: 'WRDS 2027',
    description: '',
    call_for_papers: '',
    call_for_papers_url: null,
    website: null,
    banner_image_url: null,
    location_type: 'online',
    venue_name: null,
    location: null,
    start_date: iso(300),
    end_date: iso(301),
    abstract_deadline: iso(200),
    paper_deadline: iso(230),
    review_deadline: iso(260),
    decision_date: iso(275),
    camera_ready_deadline: iso(290),
    status: 'draft',
    subject_area: 'Data Governance',
    conference_type: 'workshop',
    visibility: 'private',
    featured: false,
    public_registration: false,
    min_reviews: 2,
    enable_bidding: false,
    double_blind: true,
    show_other_reviews: false,
    keywords: [],
    allowed_file_types: 'pdf',
    billing_settled: false,
    created_by: U(1),
    created_at: iso(-3),
    updated_at: iso(-3),
  },
];

// ---------------------------------------------------------------------------
// tracks / roles
// ---------------------------------------------------------------------------

export const conference_tracks = [
  { id: uid(2100), conference_id: C(1), name: 'Machine Learning', description: 'ML theory and applications', created_at: iso(-170) },
  { id: uid(2101), conference_id: C(1), name: 'Systems & Networks', description: 'Distributed systems, networking', created_at: iso(-170) },
  { id: uid(2102), conference_id: C(1), name: 'Human-Computer Interaction', description: null, created_at: iso(-170) },
  { id: uid(2103), conference_id: C(2), name: 'General', description: null, created_at: iso(-390) },
];

const TRACKS = [uid(2100), uid(2101), uid(2102)];

export const conference_roles = (() => {
  const rows = [];
  let i = 0;
  const add = (conf, userN, role) =>
    rows.push({ id: uid(2200 + i++), conference_id: conf, user_id: U(userN), role, created_at: iso(-160) });

  add(C(1), 2, 'chair');
  add(C(1), 1, 'organizer');
  [3, 5, 6, 7, 9, 11, 13, 15].forEach((n) => add(C(1), n, 'reviewer'));
  [4, 8, 10, 11, 12, 14, 15].forEach((n) => add(C(1), n, 'author'));
  add(C(2), 2, 'chair');
  [3, 5].forEach((n) => add(C(2), n, 'reviewer'));
  [4, 10].forEach((n) => add(C(2), n, 'author'));
  add(C(3), 1, 'chair');
  return rows;
})();

// ---------------------------------------------------------------------------
// submissions
// ---------------------------------------------------------------------------

const TITLES = [
  'Federated Learning for Privacy-Preserving Medical Image Analysis in Resource-Constrained Clinical Environments',
  'A Lightweight Intrusion Detection Framework for Industrial IoT Networks',
  'Graph Neural Networks for Traffic Forecasting in North African Urban Corridors',
  'Towards Reproducible Benchmarks in Arabic Natural Language Processing',
  'Energy-Aware Task Scheduling in Heterogeneous Edge Computing Clusters',
  'On the Robustness of Transformer Architectures Under Distribution Shift: An Empirical Study Across Twelve Benchmark Datasets and Three Model Families',
  'Semantic Caching Strategies for Retrieval-Augmented Generation Systems',
  'A Formal Verification Approach for Smart Contract Access Control',
  'Explainable Anomaly Detection in Photovoltaic Plant Telemetry',
  'Data Sovereignty Requirements for National Research Infrastructures',
  'Cross-Lingual Transfer for Low-Resource Amazigh Speech Recognition',
  'Adaptive Sampling for Large-Scale Wireless Sensor Deployments',
  'نحو إطار عمل موحد لمعالجة اللغة العربية في البحث العلمي',
  'Differential Privacy in Longitudinal Student Performance Datasets',
  'A Survey of Consensus Mechanisms for Permissioned Blockchains',
  'Real-Time Pose Estimation on Embedded ARM Platforms',
  'Curriculum Learning Strategies for Medical Text Classification',
  'Hybrid CNN-LSTM Models for Short-Term Load Forecasting',
  'Automated Grading of Programming Assignments Using Static Analysis',
  'Fault Tolerance Patterns in Microservice Orchestration',
  'Multi-Objective Optimization for Cloud Resource Allocation',
  'Detecting Coordinated Inauthentic Behaviour in Academic Citation Networks',
  'A Comparative Study of Vector Databases for Semantic Search',
  'Zero-Shot Classification of Scientific Abstracts by Discipline',
  'Secure Aggregation Protocols for Collaborative Model Training',
];

const KEYWORDS = [
  ['federated learning', 'privacy', 'medical imaging'],
  ['IoT', 'security', 'intrusion detection'],
  ['graph neural networks', 'forecasting'],
  ['NLP', 'Arabic', 'benchmarks'],
  ['edge computing', 'scheduling'],
];

const SUB_STATUS = ['submitted', 'under_review', 'under_review', 'under_review', 'accepted', 'rejected', 'revision_required'];

const AUTHOR_POOL = [4, 8, 10, 11, 12, 14, 15];

export const conference_submissions = TITLES.map((title, i) => {
  const n = i + 1;
  // Paper 6 gets 8 co-authors to stress author-list layout.
  const coAuthorCount = n === 6 ? 8 : n % 4;
  const co_authors = Array.from({ length: coAuthorCount }, (_, k) => ({
    name: `${['Yassine', 'Meriem', 'Anis', 'Lina', 'Omar', 'Nour', 'Riad', 'Selma'][k % 8]} ${
      ['Belaid', 'Chikhi', 'Dahmani', 'Ferradji', 'Guerrouf', 'Hamidi', 'Idir', 'Kaci'][k % 8]
    }`,
    email: `coauthor${k}@univ.dz`,
    affiliation: k % 2 === 0 ? 'USTHB' : 'University of Oran 1',
  }));

  const status = n <= 20 ? SUB_STATUS[i % SUB_STATUS.length] : 'submitted';

  return {
    id: uid(3000 + n),
    conference_id: C(1),
    user_id: U(AUTHOR_POOL[i % AUTHOR_POOL.length]),
    track_id: TRACKS[i % TRACKS.length],
    paper_number: n,
    title,
    abstract:
      'This paper presents an approach to the problem outlined in the title. We describe the method, evaluate it on public datasets, and discuss the limitations of the current implementation together with directions for future work.',
    keywords: KEYWORDS[i % KEYWORDS.length],
    pdf_url: 'https://example.org/mock-paper.pdf',
    pdf_file_name: `paper_${n}.pdf`,
    pdf_file_size: 380000 + n * 15000,
    supplementary_files: [],
    // One submission deliberately has no PDF, to exercise the missing-file state.
    camera_ready_url: status === 'accepted' ? 'https://example.org/mock-camera-ready.docx' : null,
    camera_ready_file_name: status === 'accepted' ? `paper_${n}_camera_ready.docx` : null,
    camera_ready_submitted_at: status === 'accepted' && n === 19 ? iso(-3) : null,
    // Paper #19 has its permission form in; #5 and #12 still owe one.
    permission_form_url: status === 'accepted' && n === 19 ? 'https://example.org/mock-permission.pdf' : null,
    permission_form_file_name: status === 'accepted' && n === 19 ? 'permission_19.pdf' : null,
    permission_form_file_size: status === 'accepted' && n === 19 ? 240000 : null,
    permission_form_submitted_at: status === 'accepted' && n === 19 ? iso(-3) : null,
    co_authors,
    status,
    // Accepted-with-conditions. Two of the accepted papers carry a revision so
    // the chair and author views have something real to render.
    revision_severity: status === 'accepted' ? ({ 5: 'minor', 12: 'major' }[n] || null) : null,
    revision_note: status === 'accepted' && (n === 5 || n === 12)
      ? 'Please expand the evaluation section and clarify the threat model before the camera-ready.'
      : null,
    revision_requested_at: status === 'accepted' && (n === 5 || n === 12) ? iso(-4) : null,
    submitted_at: iso(-30 + i),
    created_at: iso(-40 + i),
    updated_at: iso(-5),
  };
}).concat([
  // A draft that was never submitted — appears only to its author.
  {
    id: uid(3100),
    conference_id: C(1),
    user_id: U(4),
    track_id: TRACKS[0],
    paper_number: null,
    title: 'Untitled draft',
    abstract: '',
    keywords: [],
    pdf_url: null,
    pdf_file_name: null,
    pdf_file_size: null,
    supplementary_files: [],
    camera_ready_url: null,
    co_authors: [],
    status: 'draft',
    submitted_at: null,
    created_at: iso(-2),
    updated_at: iso(-2),
  },
]);

// ---------------------------------------------------------------------------
// review criteria + reviews + answers
// ---------------------------------------------------------------------------

export const review_criteria = [
  {
    id: uid(4001), conference_id: C(1), question_text: 'Originality of the contribution',
    question_type: 'scale', help_text: 'How novel is the contribution relative to prior art?',
    is_required: true, display_order: 1, scale_min: 1, scale_max: 10,
    scale_min_label: 'Not original', scale_max_label: 'Highly original',
    options: null, created_at: iso(-160), updated_at: iso(-160),
  },
  {
    id: uid(4002), conference_id: C(1), question_text: 'Technical soundness',
    question_type: 'scale', help_text: null, is_required: true, display_order: 2,
    scale_min: 1, scale_max: 10, scale_min_label: 'Weak', scale_max_label: 'Rigorous',
    options: null, created_at: iso(-160), updated_at: iso(-160),
  },
  {
    id: uid(4003), conference_id: C(1), question_text: 'Is the paper within the scope of the conference?',
    question_type: 'true_false', help_text: null, is_required: true, display_order: 3,
    scale_min: null, scale_max: null, scale_min_label: null, scale_max_label: null,
    options: null, created_at: iso(-160), updated_at: iso(-160),
  },
  {
    id: uid(4004), conference_id: C(1), question_text: 'Main strengths',
    question_type: 'long_text', help_text: 'Visible to the authors.', is_required: true,
    display_order: 4, scale_min: null, scale_max: null, scale_min_label: null,
    scale_max_label: null, options: null, created_at: iso(-160), updated_at: iso(-160),
  },
  {
    id: uid(4005), conference_id: C(1), question_text: 'Main weaknesses',
    question_type: 'long_text', help_text: 'Visible to the authors.', is_required: true,
    display_order: 5, scale_min: null, scale_max: null, scale_min_label: null,
    scale_max_label: null, options: null, created_at: iso(-160), updated_at: iso(-160),
  },
  {
    id: uid(4006), conference_id: C(1), question_text: 'Confidential remarks to the chair',
    question_type: 'short_text', help_text: 'Not shared with authors.', is_required: false,
    display_order: 6, scale_min: null, scale_max: null, scale_min_label: null,
    scale_max_label: null, options: null, created_at: iso(-160), updated_at: iso(-160),
  },
];

const REVIEWERS = [3, 5, 6, 7, 9, 11, 13, 15];
const DECISIONS = ['accept', 'minor_revision', 'major_revision', 'reject'];

export const submission_reviews = (() => {
  const rows = [];
  let i = 0;
  conference_submissions.forEach((sub, si) => {
    if (sub.status === 'draft' || sub.status === 'submitted') return; // no reviews yet
    // Vary completed-review counts: 0..3, so progress indicators show every state.
    const count = (si % 4);
    for (let k = 0; k < count; k++) {
      const reviewerN = REVIEWERS[(si + k) % REVIEWERS.length];
      const isDraft = k === count - 1 && si % 5 === 0; // some in-progress reviews
      rows.push({
        id: uid(5000 + i++),
        submission_id: sub.id,
        reviewer_id: U(reviewerN),
        conference_id: C(1),
        overall_score: isDraft ? null : 3 + ((si + k) % 8),
        originality_score: isDraft ? null : 3 + ((si + k) % 7),
        technical_quality_score: isDraft ? null : 4 + ((si + k) % 6),
        clarity_score: isDraft ? null : 3 + ((si + k) % 7),
        relevance_score: isDraft ? null : 5 + ((si + k) % 5),
        detailed_feedback: isDraft
          ? 'Partial notes — still reading section 4.'
          : 'The paper is generally well written. The experimental section would benefit from an ablation study, and the comparison against baseline methods should report variance across runs.',
        strengths: isDraft ? null : 'Clear problem statement; reproducible setup; relevant to the conference scope.',
        weaknesses: isDraft ? null : 'Limited evaluation; related work misses several recent contributions.',
        questions_for_authors: isDraft ? null : 'How does the method behave when the input distribution shifts at inference time?',
        general_comments: null,
        decision: isDraft ? null : DECISIONS[(si + k) % DECISIONS.length],
        confidence_level: isDraft ? null : 2 + ((si + k) % 4),
        review_time_minutes: isDraft ? 25 : 60 + ((si * 7 + k) % 90),
        is_draft: isDraft,
        submitted_at: isDraft ? null : iso(-12 + k),
        created_at: iso(-20 + k),
        updated_at: iso(-6),
      });
    }
  });

  // ---------------------------------------------------------------------
  // Guaranteed workload for the demo reviewer (U(3), "Reviewer" in the dev
  // switcher). The round-robin above only lands on them occasionally, which
  // left the reviewer dashboard with a single row — not enough to exercise
  // "what do I owe", overdue styling, or in-progress drafts.
  // ---------------------------------------------------------------------
  const reviewable = conference_submissions.filter(
    (s) => s.status === 'under_review' || s.status === 'accepted' || s.status === 'rejected'
  );
  const already = new Set(rows.filter((r) => r.reviewer_id === U(3)).map((r) => r.submission_id));
  // [submitted?, draft?, daysFromNow for created] — mix of done, in-progress, untouched.
  const PLAN = [
    { done: true,  draft: false, age: -18 },
    { done: true,  draft: false, age: -15 },
    { done: false, draft: true,  age: -6  },  // started, not submitted
    { done: false, draft: true,  age: -3  },
    { done: false, draft: true,  age: -1  },  // barely begun
    { done: false, draft: true,  age: -30 },  // long overdue
  ];
  let p = 0;
  for (const sub of reviewable) {
    if (p >= PLAN.length) break;
    if (already.has(sub.id)) continue;
    const { done, draft, age } = PLAN[p];
    rows.push({
      id: uid(5900 + p),
      submission_id: sub.id,
      reviewer_id: U(3),
      conference_id: sub.conference_id,
      overall_score: done ? 5 + (p % 4) : null,
      originality_score: done ? 4 + (p % 5) : null,
      technical_quality_score: done ? 5 + (p % 4) : null,
      clarity_score: done ? 4 + (p % 5) : null,
      relevance_score: done ? 6 + (p % 3) : null,
      detailed_feedback: done
        ? 'Solid contribution. The evaluation is convincing on the two larger datasets, though the ablation in Table 3 needs error bars before the claim in §5.2 is fully supported.'
        : (draft ? 'Read through §3 so far. Method looks sound; need to check the proofs in the appendix.' : null),
      strengths: done ? 'Well-motivated problem; careful experimental protocol; honest about limitations.' : null,
      weaknesses: done ? 'Related work is thin on the 2024–2025 literature; no variance reported across seeds.' : null,
      questions_for_authors: done ? 'Did you tune the learning rate per dataset, or use a single schedule throughout?' : null,
      general_comments: null,
      decision: done ? DECISIONS[p % DECISIONS.length] : null,
      confidence_level: done ? 3 + (p % 3) : null,
      review_time_minutes: done ? 95 + p * 12 : 20,
      is_draft: !done,
      submitted_at: done ? iso(age + 3) : null,
      created_at: iso(age),
      updated_at: iso(done ? age + 3 : age),
    });
    p += 1;
  }

  return rows;
})();

export const review_answers = (() => {
  const rows = [];
  let i = 0;
  submission_reviews.forEach((rev) => {
    if (rev.is_draft) return;
    review_criteria.forEach((crit, ci) => {
      rows.push({
        id: uid(6000 + i++),
        submission_id: rev.submission_id,
        reviewer_id: rev.reviewer_id,
        criteria_id: crit.id,
        answer_numeric: crit.question_type === 'scale' ? 4 + (i % 6) : null,
        answer_boolean: crit.question_type === 'true_false' ? i % 4 !== 0 : null,
        answer_text: crit.question_type.includes('text')
          ? 'The contribution is relevant and the presentation is adequate for the venue.'
          : null,
        created_at: iso(-12),
        updated_at: iso(-12),
      });
    });
  });
  return rows;
})();

// ---------------------------------------------------------------------------
// notifications / announcements
// ---------------------------------------------------------------------------

export const notification_templates = [
  { id: uid(7001), conference_id: C(1), created_by: U(2), name: 'Reviewer assignment', notification_type: 'assignment', event_key: 'reviewer_assigned', subject: 'You have been assigned a paper to review', message: 'Dear {{reviewer_name}}, you have been assigned paper #{{paper_number}}.', is_default: true, created_at: iso(-150), updated_at: iso(-150) },
  { id: uid(7002), conference_id: C(1), created_by: U(2), name: 'Acceptance notice', notification_type: 'decision', event_key: 'decision_accept', subject: 'Decision on your submission to {{conference_short_name}}', message: 'We are pleased to inform you that your paper has been accepted.', is_default: true, created_at: iso(-150), updated_at: iso(-150) },
  { id: uid(7003), conference_id: C(1), created_by: U(2), name: 'Review deadline reminder', notification_type: 'deadline_reminder', event_key: 'review_deadline', subject: 'Review deadline approaching', message: 'A reminder that reviews are due on {{review_deadline}}.', is_default: false, created_at: iso(-140), updated_at: iso(-140) },
];

export const notifications = [
  { id: uid(7101), conference_id: C(1), template_id: uid(7001), created_by: U(2), notification_type: 'assignment', subject: 'You have been assigned 3 papers to review', message: 'Please complete your reviews before the deadline.', recipient_type: 'all_reviewers', recipient_filter: {}, status: 'sent', scheduled_at: null, sent_at: iso(-18), total_recipients: 8, successful_sends: 8, failed_sends: 0, created_at: iso(-18), updated_at: iso(-18) },
  { id: uid(7102), conference_id: C(1), template_id: uid(7003), created_by: U(2), notification_type: 'deadline_reminder', subject: 'Review deadline approaching', message: 'Reviews are due in 14 days.', recipient_type: 'all_reviewers', recipient_filter: {}, status: 'scheduled', scheduled_at: iso(7), sent_at: null, total_recipients: 8, successful_sends: 0, failed_sends: 0, created_at: iso(-4), updated_at: iso(-4) },
  { id: uid(7103), conference_id: C(1), template_id: null, created_by: U(2), notification_type: 'custom', subject: 'Venue update for ICACDS 2026', message: 'The conference venue has been confirmed.', recipient_type: 'all_authors', recipient_filter: {}, status: 'draft', scheduled_at: null, sent_at: null, total_recipients: 0, successful_sends: 0, failed_sends: 0, created_at: iso(-1), updated_at: iso(-1) },
  { id: uid(7104), conference_id: C(1), template_id: null, created_by: U(2), notification_type: 'status_update', subject: 'Submission received', message: 'Your submission has been received.', recipient_type: 'specific_users', recipient_filter: {}, status: 'failed', scheduled_at: null, sent_at: iso(-9), total_recipients: 4, successful_sends: 1, failed_sends: 3, created_at: iso(-9), updated_at: iso(-9) },
];

export const notification_recipients = [4, 8, 10, 11, 12].map((n, i) => ({
  id: uid(7200 + i),
  notification_id: uid(7101),
  user_id: U(n),
  status: i === 3 ? 'failed' : 'sent',
  sent_at: iso(-18),
  read_at: i < 2 ? iso(-17) : null,
  error_message: i === 3 ? 'Mailbox unavailable' : null,
  created_at: iso(-18),
}));

export const conference_announcements = [
  { id: uid(7301), conference_id: C(1), title: 'Review phase is now open', body: 'All assigned reviewers can now access their queues. Reviews are due in two weeks.', group_ids: [], audience_op: 'any', is_pinned: true, notification_id: null, created_by: U(2), published_at: iso(-18), created_at: iso(-18), updated_at: iso(-18) },
  { id: uid(7302), conference_id: C(1), title: 'Submission deadline extended by five days', body: 'Following several requests, the paper deadline has been extended.', group_ids: [], audience_op: 'any', is_pinned: false, notification_id: null, created_by: U(2), published_at: iso(-32), created_at: iso(-32), updated_at: iso(-32) },
];

// ---------------------------------------------------------------------------
// schedule
// ---------------------------------------------------------------------------

export const conference_rooms = [
  { id: uid(8001), conference_id: C(1), name: 'Auditorium A', location: 'Ground floor', capacity: 300, sort_order: 1, created_at: iso(-60), updated_at: iso(-60) },
  { id: uid(8002), conference_id: C(1), name: 'Room B2', location: 'First floor', capacity: 80, sort_order: 2, created_at: iso(-60), updated_at: iso(-60) },
];

export const conference_sessions = [
  { id: uid(8101), conference_id: C(1), room_id: uid(8001), track_id: TRACKS[0], title: 'Opening & Keynote', description: 'Welcome address followed by the opening keynote.', session_type: 'opening', session_date: '2026-10-07', start_time: '09:00', end_time: '10:30', chair_name: 'Nadia Cherif', is_published: true, sort_order: 1, created_at: iso(-30), updated_at: iso(-30) },
  { id: uid(8102), conference_id: C(1), room_id: uid(8001), track_id: TRACKS[0], title: 'Machine Learning I', description: null, session_type: 'paper', session_date: '2026-10-07', start_time: '11:00', end_time: '12:30', chair_name: 'Karim Haddad', is_published: true, sort_order: 2, created_at: iso(-30), updated_at: iso(-30) },
  { id: uid(8103), conference_id: C(1), room_id: uid(8002), track_id: TRACKS[1], title: 'Systems & Networks', description: null, session_type: 'paper', session_date: '2026-10-07', start_time: '11:00', end_time: '12:30', chair_name: null, is_published: false, sort_order: 3, created_at: iso(-30), updated_at: iso(-30) },
];

export const session_items = [
  { id: uid(8201), session_id: uid(8102), submission_id: uid(3005), presentation_order: 1, start_time: '11:00', duration_minutes: 25, custom_title: null, custom_speaker: null, custom_affiliation: null, item_type: 'paper', created_at: iso(-30) },
  { id: uid(8202), session_id: uid(8102), submission_id: uid(3003), presentation_order: 2, start_time: '11:25', duration_minutes: 25, custom_title: null, custom_speaker: null, custom_affiliation: null, item_type: 'paper', created_at: iso(-30) },
  { id: uid(8203), session_id: uid(8101), submission_id: null, presentation_order: 1, start_time: '09:15', duration_minutes: 60, custom_title: 'Keynote: Research Infrastructure and Data Sovereignty', custom_speaker: 'Prof. Julia Novak', custom_affiliation: 'Technical University of Munich', item_type: 'keynote', created_at: iso(-30) },
];

// ---------------------------------------------------------------------------
// groups / invitations / participants
// ---------------------------------------------------------------------------

export const conference_participant_groups = [
  { id: uid(9001), conference_id: C(1), name: 'Programme Committee', description: 'Senior reviewers', role_sources: ['reviewer'], created_by: U(2), created_at: iso(-100), updated_at: iso(-100) },
  { id: uid(9002), conference_id: C(1), name: 'Accepted authors', description: null, role_sources: ['author'], created_by: U(2), created_at: iso(-100), updated_at: iso(-100) },
];

export const conference_group_members = [3, 5, 6, 7].map((n, i) => ({
  id: uid(9100 + i), group_id: uid(9001), user_id: U(n), created_at: iso(-100),
}));

export const conference_invitations = [
  { id: uid(9201), conference_id: C(1), email: 'j.novak@tum.de', role: 'reviewer', invited_by: U(2), status: 'pending', created_at: iso(-7) },
  { id: uid(9202), conference_id: C(1), email: 'new.reviewer@univ.dz', role: 'reviewer', invited_by: U(2), status: 'pending', created_at: iso(-3) },
  { id: uid(9203), conference_id: C(1), email: 'i.berrada@um5.ac.ma', role: 'reviewer', invited_by: U(2), status: 'accepted', created_at: iso(-40) },
];

export const conference_participants = AUTHOR_POOL.map((n, i) => ({
  id: uid(9300 + i), conference_id: C(1), user_id: U(n), participant_number: i + 1, created_at: iso(-30),
}));

export const user_login_logs = Array.from({ length: 12 }, (_, i) => ({
  id: uid(9400 + i),
  user_id: U((i % 6) + 1),
  ip_address: `41.200.${i}.${10 + i}`,
  country: pick(['Algeria', 'France', 'Morocco', 'Germany']),
  country_code: 'DZ',
  city: pick(['Algiers', 'Oran', 'Constantine', 'Paris']),
  region: 'Center',
  latitude: 36.75, longitude: 3.06, timezone: 'Africa/Algiers',
  browser: pick(['Chrome 128', 'Firefox 130', 'Safari 18']),
  os: pick(['Windows 11', 'macOS 15', 'Ubuntu 24.04']),
  device_type: i % 5 === 0 ? 'mobile' : 'desktop',
  logged_in_at: iso(-i),
}));

// ---------------------------------------------------------------------------
// billing
// ---------------------------------------------------------------------------

export const billing_plans = [
  { id: uid(9501), name: 'Single conference', plan_type: 'credits', credits: 1, duration_days: null, price_dzd: 15000, description: 'One conference publication.', is_active: true, sort_order: 1, created_at: iso(-200), updated_at: iso(-200) },
  { id: uid(9502), name: 'Institutional annual', plan_type: 'annual', credits: null, duration_days: 365, price_dzd: 120000, description: 'Unlimited conferences for one year.', is_active: true, sort_order: 2, created_at: iso(-200), updated_at: iso(-200) },
];

export const payment_settings = [
  { id: 1, ccp_number: '0012345678 key 91', rip_number: '00799999001234567891', baridimob_number: '00799999001234567891', instructions: 'Include your reference code in the transfer note.', updated_at: iso(-50) },
];

export const user_billing = [
  { user_id: U(1), free_publishes_remaining: 1, credits: 5, annual_until: iso(200), updated_at: iso(-10) },
  { user_id: U(2), free_publishes_remaining: 0, credits: 2, annual_until: null, updated_at: iso(-10) },
  { user_id: U(4), free_publishes_remaining: 1, credits: 0, annual_until: null, updated_at: iso(-10) },
];

export const payments = [
  { id: uid(9601), user_id: U(2), plan_id: uid(9501), amount_dzd: 15000, receipt_url: 'https://example.org/receipt.pdf', reference_code: 'CMP-4821', status: 'pending', admin_note: null, reviewed_by: null, created_at: iso(-2), reviewed_at: null },
  { id: uid(9602), user_id: U(4), plan_id: uid(9502), amount_dzd: 120000, receipt_url: 'https://example.org/receipt2.pdf', reference_code: 'CMP-4790', status: 'approved', admin_note: 'Verified against CCP statement.', reviewed_by: U(1), created_at: iso(-30), reviewed_at: iso(-28) },
  { id: uid(9603), user_id: U(11), plan_id: uid(9501), amount_dzd: 15000, receipt_url: null, reference_code: 'CMP-4755', status: 'rejected', admin_note: 'No receipt attached.', reviewed_by: U(1), created_at: iso(-45), reviewed_at: iso(-44) },
];

// ---------------------------------------------------------------------------
// assembled database
// ---------------------------------------------------------------------------

export const buildSeed = () => ({
  user_profiles,
  conferences,
  conference_roles,
  conference_tracks,
  conference_submissions,
  submission_reviews,
  review_criteria,
  review_answers,
  notification_templates,
  notifications,
  notification_recipients,
  conference_rooms,
  conference_sessions,
  session_items,
  conference_invitations,
  conference_participants,
  conference_participant_groups,
  conference_group_members,
  conference_announcements,
  user_login_logs,
  billing_plans,
  payment_settings,
  user_billing,
  payments,
});

/**
 * Foreign-key map for PostgREST-style embedded selects, e.g.
 *   .select('*, user_profiles(first_name, last_name)')
 * Keyed by [table][embedName]. `many: true` returns an array.
 */
export const RELATIONS = {
  conference_submissions: {
    conferences: { table: 'conferences', localKey: 'conference_id', foreignKey: 'id' },
    user_profiles: { table: 'user_profiles', localKey: 'user_id', foreignKey: 'id' },
    conference_tracks: { table: 'conference_tracks', localKey: 'track_id', foreignKey: 'id' },
    submission_reviews: { table: 'submission_reviews', localKey: 'id', foreignKey: 'submission_id', many: true },
  },
  submission_reviews: {
    user_profiles: { table: 'user_profiles', localKey: 'reviewer_id', foreignKey: 'id' },
    conference_submissions: { table: 'conference_submissions', localKey: 'submission_id', foreignKey: 'id' },
    conferences: { table: 'conferences', localKey: 'conference_id', foreignKey: 'id' },
  },
  conference_roles: {
    user_profiles: { table: 'user_profiles', localKey: 'user_id', foreignKey: 'id' },
    conferences: { table: 'conferences', localKey: 'conference_id', foreignKey: 'id' },
  },
  notification_recipients: {
    notifications: { table: 'notifications', localKey: 'notification_id', foreignKey: 'id' },
    user_profiles: { table: 'user_profiles', localKey: 'user_id', foreignKey: 'id' },
  },
  notifications: {
    conferences: { table: 'conferences', localKey: 'conference_id', foreignKey: 'id' },
    notification_templates: { table: 'notification_templates', localKey: 'template_id', foreignKey: 'id' },
  },
  payments: {
    billing_plans: { table: 'billing_plans', localKey: 'plan_id', foreignKey: 'id' },
    user_profiles: { table: 'user_profiles', localKey: 'user_id', foreignKey: 'id' },
  },
  conference_participant_groups: {
    conference_group_members: { table: 'conference_group_members', localKey: 'id', foreignKey: 'group_id', many: true },
  },
  conference_group_members: {
    user_profiles: { table: 'user_profiles', localKey: 'user_id', foreignKey: 'id' },
  },
  session_items: {
    conference_sessions: { table: 'conference_sessions', localKey: 'session_id', foreignKey: 'id' },
    conference_submissions: { table: 'conference_submissions', localKey: 'submission_id', foreignKey: 'id' },
  },
  conference_sessions: {
    conference_rooms: { table: 'conference_rooms', localKey: 'room_id', foreignKey: 'id' },
    session_items: { table: 'session_items', localKey: 'id', foreignKey: 'session_id', many: true },
  },
  conference_announcements: {
    conferences: { table: 'conferences', localKey: 'conference_id', foreignKey: 'id' },
  },
  conference_invitations: {
    conferences: { table: 'conferences', localKey: 'conference_id', foreignKey: 'id' },
  },
  conference_participants: {
    conferences: { table: 'conferences', localKey: 'conference_id', foreignKey: 'id' },
    user_profiles: { table: 'user_profiles', localKey: 'user_id', foreignKey: 'id' },
  },
  user_billing: {
    user_profiles: { table: 'user_profiles', localKey: 'user_id', foreignKey: 'id' },
  },
};
